import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  starteAnalyse,
  fetchAnalyseHistorie,
  analyseZuruecksetzen,
  selectAnalyseErgebnis,
  selectAnalyseStatus,
  selectAnalyseError,
  selectAnalyseFehlerDetails,
  selectAnalyseHistorie,
} from 'modules/analyse/features/analyseSlice';
import { projectVisited } from 'core/features/projects/projectsSlice';
import BackToOverview from 'core/components/BackToOverview';

/** Ampel-Farben passend zu den Design-Tokens. */
const AMPEL = {
  GRUEN: { text: 'text-gain', bg: 'bg-gain/10', dot: 'bg-gain' },
  GELB: { text: 'text-amber-600', bg: 'bg-amber-500/10', dot: 'bg-amber-500' },
  ROT: { text: 'text-loss', bg: 'bg-loss/10', dot: 'bg-loss' },
  GRAU: { text: 'text-ink-muted', bg: 'bg-ink/5', dot: 'bg-ink-muted' },
};
const ampelStyle = (ampel) => AMPEL[ampel] || AMPEL.GRAU;

/** Ampel des Gesamturteils aus dem Score ableiten. */
const ampelVonScore = (score) => {
  if (score == null) return 'GRAU';
  if (score >= 60) return 'GRUEN';
  return score >= 40 ? 'GELB' : 'ROT';
};

/** Karte einer einzelnen Teilbewertung. */
function TeilbewertungKarte({ bewertung }) {
  const stil = ampelStyle(bewertung.ampel);
  return (
    <div className="rounded-xl border border-line bg-paper-soft p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-serif text-lg font-semibold text-ink">{bewertung.kategorie}</h3>
        <span className={`inline-flex items-center gap-1.5 font-mono text-sm font-semibold ${stil.text}`}>
          <span className={`h-2.5 w-2.5 rounded-full ${stil.dot}`} />
          {bewertung.score != null ? `${bewertung.score}/100` : '–'}
        </span>
      </div>
      <p className="mt-2 text-sm text-ink-soft">{bewertung.begruendung}</p>
      <p className="mt-2 font-mono text-xs text-ink-muted">Quelle: {bewertung.quelle}</p>
    </div>
  );
}

/** Banner mit dem verdichteten Gesamturteil. */
function GesamtBanner({ ergebnis }) {
  const stil = ampelStyle(ampelVonScore(ergebnis.gesamtScore));
  return (
    <div className={`rounded-2xl border border-line ${stil.bg} p-6`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="font-mono text-xs uppercase tracking-wider text-ink-muted">Gesamturteil</p>
          <h2 className={`font-serif text-3xl font-semibold ${stil.text}`}>{ergebnis.einstufung}</h2>
        </div>
        <span className={`font-mono text-2xl font-bold ${stil.text}`}>
          {ergebnis.gesamtScore != null ? `${ergebnis.gesamtScore}/100` : '–'}
        </span>
      </div>
      <p className="mt-3 text-sm text-ink-soft">{ergebnis.zusammenfassung}</p>
      <p className="mt-2 font-mono text-xs text-ink-muted">
        {ergebnis.symbol ? `Symbol ${ergebnis.symbol} · ` : ''}ISIN {ergebnis.isin}
      </p>
    </div>
  );
}

/**
 * Aktien-Check: Eingabe von Name + ISIN, danach eine Live-Tiefenanalyse aus
 * mehreren kostenlosen Quellen (Kurse, Fundamentaldaten, Analystenmeinung,
 * Reddit-Buzz) mit Gesamturteil, Teilbewertungen und früheren Analysen.
 */
export default function AktienCheckPage() {
  const dispatch = useDispatch();
  const ergebnis = useSelector(selectAnalyseErgebnis);
  const status = useSelector(selectAnalyseStatus);
  const error = useSelector(selectAnalyseError);
  const fehlerDetails = useSelector(selectAnalyseFehlerDetails);
  const historie = useSelector(selectAnalyseHistorie);

  const [name, setName] = useState('');
  const [isin, setIsin] = useState('');

  useEffect(() => {
    dispatch(projectVisited('aktien-check'));
    return () => dispatch(analyseZuruecksetzen());
  }, [dispatch]);

  const onSubmit = async (event) => {
    event.preventDefault();
    const isinBereinigt = isin.trim().toUpperCase();
    if (!isinBereinigt) {
      return;
    }
    const aktion = await dispatch(starteAnalyse({ name: name.trim(), isin: isinBereinigt }));
    if (starteAnalyse.fulfilled.match(aktion)) {
      dispatch(fetchAnalyseHistorie(isinBereinigt));
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
      <div className="mb-6">
        <BackToOverview />
      </div>

      <header className="mb-6">
        <h1 className="font-serif text-3xl font-semibold text-ink">Aktien-Check</h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-muted">
          Name und ISIN eingeben – der Service wertet die Aktie über mehrere kostenlose
          Online-Quellen (Kurse, Fundamentaldaten, Analystenschätzungen) und den Reddit-Buzz
          aus und liefert ein Gesamturteil.
        </p>
      </header>

      <form onSubmit={onSubmit} className="mb-8 grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <label className="block">
          <span className="mb-1 block font-mono text-xs uppercase tracking-wider text-ink-muted">
            Aktienname (optional)
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Apple"
            className="w-full rounded-lg border border-line bg-paper-sunken px-3 py-2 text-ink placeholder:text-ink-muted focus:border-brand-500 focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="mb-1 block font-mono text-xs uppercase tracking-wider text-ink-muted">
            ISIN
          </span>
          <input
            type="text"
            value={isin}
            onChange={(e) => setIsin(e.target.value)}
            placeholder="z. B. US0378331005"
            required
            className="w-full rounded-lg border border-line bg-paper-sunken px-3 py-2 font-mono text-ink placeholder:text-ink-muted focus:border-brand-500 focus:outline-none"
          />
        </label>
        <button
          type="submit"
          disabled={status === 'loading'}
          className="rounded-lg bg-brand-600 px-5 py-2 font-medium text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'loading' ? 'Analysiere…' : 'Analysieren'}
        </button>
      </form>

      {status === 'loading' && (
        <p className="text-ink-muted">Analyse läuft – mehrere Quellen werden live abgefragt…</p>
      )}
      {status === 'failed' && (
        <div className="space-y-3 rounded-xl border border-loss/30 bg-loss/5 p-6">
          <h2 className="font-serif text-xl font-semibold text-loss">Analyse fehlgeschlagen</h2>
          <p className="text-sm text-ink-soft">{error || 'Unbekannter Fehler.'}</p>
          {fehlerDetails && (
            <div className="space-y-2">
              <p className="font-mono text-xs text-ink-muted">
                Quelle: {fehlerDetails.quelle}
                {fehlerDetails.httpStatus != null ? ` · HTTP-Status: ${fehlerDetails.httpStatus}` : ''}
              </p>
              {fehlerDetails.responseBody && (
                <div>
                  <p className="mb-1 font-mono text-xs uppercase tracking-wider text-ink-muted">
                    Antwortbody
                  </p>
                  <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-line bg-paper-sunken p-3 font-mono text-xs text-ink-soft">
                    {fehlerDetails.responseBody}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {status === 'succeeded' && ergebnis && (
        <div className="space-y-6">
          <GesamtBanner ergebnis={ergebnis} />

          <div className="grid gap-4 sm:grid-cols-2">
            {(ergebnis.teilbewertungen || []).map((b) => (
              <TeilbewertungKarte key={b.kategorie} bewertung={b} />
            ))}
          </div>

          {ergebnis.datenluecken && ergebnis.datenluecken.length > 0 && (
            <div className="rounded-xl border border-line bg-paper-soft p-4">
              <h3 className="font-serif text-base font-semibold text-ink">Datenlücken</h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ink-muted">
                {ergebnis.datenluecken.map((luecke) => (
                  <li key={luecke}>{luecke}</li>
                ))}
              </ul>
            </div>
          )}

          {historie.length > 1 && (
            <div>
              <h3 className="mb-2 font-serif text-lg font-semibold text-ink">Frühere Analysen</h3>
              <div className="overflow-hidden rounded-xl border border-line bg-paper-soft">
                {historie.map((eintrag) => {
                  const stil = ampelStyle(ampelVonScore(eintrag.gesamtScore));
                  return (
                    <div
                      key={eintrag.id}
                      className="flex items-center justify-between border-t border-line px-4 py-3 first:border-t-0"
                    >
                      <span className="font-mono text-xs text-ink-muted">
                        {new Date(eintrag.erstelltAm).toLocaleString('de-DE')}
                      </span>
                      <span className={`font-mono text-sm font-semibold ${stil.text}`}>
                        {eintrag.einstufung}
                        {eintrag.gesamtScore != null ? ` (${eintrag.gesamtScore})` : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
